import { useEffect, useRef } from 'react'
import { pushViewItemList } from './events'
import { whenPageReady } from './state'
import { ProductItem } from './types'

interface ViewItemListParams {
  items: ProductItem[]
  listId: string
  listName: string
  /**
   * `false` per le liste montate ma non mostrate. I listing tengono in pagina il
   * carosello "nessun risultato" nascosto via CSS: renderizzato ma mai visto,
   * non è un view_item_list.
   */
  enabled?: boolean
}

/**
 * Emette `view_item_list` quando la lista mostrata cambia davvero.
 *
 * I listing si ri-renderizzano a ogni cambio di filtro o di pagina e l'array
 * `items` è ricostruito ogni volta: la firma sugli id evita di ripetere lo
 * stesso evento a ogni render mantenendo il push su un cambio reale di risultati.
 *
 * L'evento passa da `whenPageReady` perché queste liste arrivano da import
 * dinamici: senza attendere il `page_view` della pagina che le contiene
 * partirebbero con il `page_data` di quella precedente.
 */
export function useViewItemList({ items, listId, listName, enabled = true }: ViewItemListParams): void {
  const lastSignature = useRef<string | null>(null)
  const queued = useRef(false)
  const latest = useRef({ items, listId, listName })

  latest.current = { items, listId, listName }
  const signature = `${listId}|${items.map(item => item.item_id).join(',')}`

  useEffect(() => {
    if (!enabled) return
    if (!items.length) return
    if (lastSignature.current === signature) return

    lastSignature.current = signature

    // Se i risultati cambiano ancora mentre siamo in coda — i filtri del listing
    // che si assestano prima dell'idratazione — non serve una seconda emissione:
    // al flush si legge comunque la lista aggiornata.
    if (queued.current) return
    queued.current = true

    whenPageReady(() => {
      queued.current = false
      pushViewItemList(latest.current)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled])
}
